import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import StarSmileLineIcon from 'remixicon-react/StarSmileLineIcon';
import { ArticleType } from '../hooks/useArticlesQuery';
import { ArticleVariantType } from './ArticleBox';
import Collaborators from 'pages/drafts/Collaborators';
import { ArticleUserMapType } from 'pages/article/hooks/useArticleDetailsQuery';
import ArticleInteractionActions from './ArticleInteractionActions';
import Chat3FillIcon from 'remixicon-react/Chat3FillIcon';
import Dot from 'components/common/dot/Dot';
import { theme } from 'config/theme';
import { formatDate } from 'utility/helpers';
import LightbulbFlashFillIcon from 'remixicon-react/LightbulbFlashFillIcon';
import Avatar from 'components/common/avatar/Avatar';
import { USER_PROFILE, VIEW_ARTICLE, VIEW_DRAFT } from 'constants/routes';
import { UserLink } from './DraftStatusMessage';
import { Link } from 'react-router-dom';

type ArticleInteractionsProps = {
  article: ArticleType;
  isDetailedView?: boolean;
  isDraftView?: boolean;
  isPublishedByBuildWiseInsights: boolean;
  isRelatedPostView?: boolean;
  searchKeyword?: string;
  variant: ArticleVariantType;
  scrollInfoKey?: string;
  userMap?: ArticleUserMapType;
  dispatch: any;
  likeHandler: (article?: ArticleType) => void;
  openInsightsBanner: () => void;
  viewInteractionsHandler: () => void;
};

function ArticleInteractions({
  article,
  isDetailedView,
  isDraftView,
  isPublishedByBuildWiseInsights,
  isRelatedPostView = false,
  searchKeyword,
  variant,
  scrollInfoKey,
  userMap,
  dispatch,
  likeHandler,
  openInsightsBanner,
  viewInteractionsHandler,
}: ArticleInteractionsProps) {
  return (
    <Container className={`article-interactions ${variant}`}>
      <Box display="flex" alignItems="center">
        {variant !== 'playlist' && (
          <Box display="flex" alignItems="center">
            {!isRelatedPostView && (
              <>
                <Box display="flex" alignItems="center" title="Created By">
                  <Avatar
                    src={article?.profileImageURL}
                    size={24}
                    fontSize={12}
                    name={isPublishedByBuildWiseInsights ? '' : article.userNameToDisplay}
                    icon={<LightbulbFlashFillIcon color={theme.palette.custom.superNova} />}
                    bgcolor={
                      isPublishedByBuildWiseInsights ? theme.palette.custom.orangeYellow : ''
                    }
                    onClick={openInsightsBanner}
                    to={
                      isPublishedByBuildWiseInsights
                        ? ''
                        : USER_PROFILE.replace(':userName', article?.createdBy || '')
                    }
                  />
                  <UserLink
                    mail={article.createdBy}
                    name={article.userNameToDisplay}
                    sx={(theme) => ({
                      color: theme.palette.custom.tuna,
                      fontSize: 14,
                      fontWeight: 500,
                      ml: 0.75,
                      textTransform: 'capitalize',
                      whiteSpace: 'nowrap',
                    })}
                    onInsightsUserClick={openInsightsBanner}
                  />
                </Box>
                <Dot />
              </>
            )}
            <Typography
              title="Created On"
              sx={(theme) => ({
                color: theme.palette.custom.mistBlue,
                fontSize: 14,
                whiteSpace: 'nowrap',
              })}
            >
              {formatDate(article.isDraft ? article.createdAt : article.publishedAt)}
            </Typography>
            {!isDetailedView && article.totalComments > 0 && (
              <>
                <Dot />
                <Box
                  display="flex"
                  alignItems="center"
                  {...(!isDetailedView && {
                    component: Link,
                    to: `${(isDraftView ? VIEW_DRAFT : VIEW_ARTICLE).replace(':articleId', article.id)}${
                      searchKeyword ? `?q=${encodeURIComponent(searchKeyword)}` : ''
                    }#comments`,
                    state: {
                      isPlayListFeature: article.originIndicator === 'PLAYLIST',
                      scrollInfoKey,
                      isDraft: article.isDraft,
                    },
                  })}
                >
                  <Chat3FillIcon size={18} color={theme.palette.custom.paleSky} />
                  <Typography
                    sx={(theme) => ({
                      color: theme.palette.custom.mistBlue,
                      fontSize: 14,
                      ml: 0.5,
                    })}
                  >
                    {article.totalComments}{' '}
                    {!isRelatedPostView && (article.totalComments > 1 ? 'Comments' : 'Comment')}
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        )}

        {article?.endorsements?.length > 0 && (
          <>
            {variant !== 'playlist' && <Dot />}
            <Tooltip placement="top" title={<Typography fontSize={14}>Endorsed</Typography>}>
              <Box
                onClick={() => {
                  setTimeout(() => {
                    dispatch({
                      type: 'SET_ARTICLE',
                      payload: {
                        selectedArticle: article,
                        selectedArticleIndex: -1,
                      },
                    });
                  }, 0);
                  dispatch({
                    type: 'TOGGLE_ENDORSE_INTERACTIONS_MODAL',
                    payload: true,
                    key: 'openEndorseInteractionsModal',
                  });
                }}
                sx={(theme) => ({
                  display: 'flex',
                  alignItems: 'center',
                  color: theme.palette.custom.jetGrey,
                  minWidth: 'fit-content',
                  fontSize: isRelatedPostView ? 14 : 16,
                })}
              >
                <EndorseIconButton>
                  <StarSmileLineIcon size={isRelatedPostView ? 14 : 16} />
                </EndorseIconButton>
                <Typography
                  sx={(theme) => ({
                    color: theme.palette.custom.mistBlue,
                    fontSize: 14,
                    ml: 0.5,
                  })}
                >
                  {article.endorsements.length}
                </Typography>
              </Box>
            </Tooltip>
          </>
        )}
        {article.isDraft && <Collaborators article={article} userMap={userMap} />}
      </Box>

      {!article.isDraft && (
        <ArticleInteractionActions
          article={article}
          isDetailedView={isDetailedView}
          isDraftView={isDraftView}
          isRelatedPostView={isRelatedPostView}
          scrollInfoKey={scrollInfoKey}
          searchKeyword={searchKeyword}
          variant={variant}
          likeHandler={likeHandler}
          viewInteractionsHandler={viewInteractionsHandler}
        />
      )}
    </Container>
  );
}

const Container = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: 12,
  paddingBottom: 12,
  '&.related-post': {
    marginTop: 0,
    padding: '8px 0 16px',
    borderBottom: `1px solid ${theme.palette.custom.divider} !important`,
  },
}));

const EndorseIconButton = styled(IconButton)(({ theme }) => ({
  padding: 4,
  width: 22,
  height: 22,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.custom.superNova,
  '&:hover': {
    backgroundColor: theme.palette.custom.superNova,
  },
}));

export const ArticleTypeLabelContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
});

export const ArticleTypeLabel = styled(Typography)(({ theme }) => ({
  marginLeft: 4,
  color: theme.palette.custom.tuna,
  fontWeight: 700,
  fontSize: 14,
}));

export default ArticleInteractions;
